import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetUrl, setResetUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/auth-forgot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Request failed');
        return;
      }
      setSuccess(true);
      if (data.reset_url || data.dev_reset_url) {
        setResetUrl(data.reset_url || data.dev_reset_url);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[440px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <Link to="/signin" className="text-text-secondary hover:text-accent transition-colors">Sign in</Link>
        <span>/</span>
        <span>Forgot password</span>
      </div>
      <h1 className="font-display text-2xl md:text-[1.8rem] font-bold mb-2">Reset Your Password</h1>
      <p className="text-text-secondary mb-6 text-sm">
        Enter your listing's email address and we'll send you a link to set your password.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {success ? (
        <div className="space-y-4">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm text-green-700 dark:text-green-300">
            ✓ If an account exists for <strong>{email}</strong>, a reset link has been generated.
          </div>
          {resetUrl && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                🔧 Dev mode - email sending not yet wired up. Click the link below to set your password:
              </p>
              <a href={resetUrl} className="text-sm text-accent hover:underline break-all">
                {resetUrl}
              </a>
            </div>
          )}
          <Link to="/signin" className="btn btn-ghost btn-sm">← Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-2">Email</label>
            <input
              className="form-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.co.nz"
              autoComplete="email"
            />
          </div>
          <button type="submit" disabled={loading} className="btn btn-primary btn-lg btn-block">
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}

      <div className="mt-6 text-center">
        <Link to="/signin" className="text-sm text-accent hover:underline">
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
}
